import React, { useState } from 'react';
import { useModal } from 'react-brave-modal';
import { format, parseISO } from 'date-fns';
import { useCurrentEffect } from '../../../../lib/use-current-effect';

import { AlertRow, AlertTriggersResponse } from '../../../../entities/Alert';
import { HealthParameterThreshold } from '../../../../entities/HealthParameter';
import AlertCategoryEnum from '../../../../enums/AlertCategoryEnum';
import AlertStateEnum from '../../../../enums/AlertStateEnum';
import { listAlertTriggers } from '../../../../services/alerts';
import getAlertLabels from '../../../../utils/getAlertLabels';
import getAppointmentOutcomeLabel from '../../../../utils/getAppointmentOutcome';
import { rulerFor } from '../../../../utils/describeThreshold';
import { alertChartConfig } from '../../../../utils/alertChart';
import Spinner from '../../../../components/Spinner';

import { TreatButton } from '../styles';
import WhatsappAction from '../WhatsappAction';
import { CANNOT_TREAT_MESSAGE } from '../TreatModal';
import MetricChart from './MetricChart';
import AnimatedHeight from './AnimatedHeight';
import {
  AnchorModalTop,
  Actions,
  Badge,
  Badges,
  ConditionList,
  Container,
  Muted,
  EmptyList,
  Note,
  PatientLink,
  SectionHeader,
  SpinnerBox,
  ViewSwitch,
  ViewOption,
  Summary,
  SummaryRow,
  TriggerList,
  TriggerRow,
  Triggers,
} from './styles';

const TRIGGERS_LIMIT = 20;
const STATE_COLOR = '#828282';

interface AlertDetailsModalProps {
  alert: AlertRow;
  thresholds: HealthParameterThreshold[];
  onOpenPatient: (alert: AlertRow) => void;
  onTreat: (alert: AlertRow) => void;
  /** Ver `canTreat` na tabela. */
  canTreat: boolean;
}

const formatDateTime = (value: string): string =>
  format(parseISO(value), "dd/MM/yyyy 'às' HH:mm");

/**
 * O informativo do alerta ("Visualizar"): o que disparou, contra qual régua, em que
 * situação ele está e o histórico das leituras que o abriram e o reforçaram.
 *
 * O prontuário continua a um clique, pelo ID — o informativo é sobre o **alerta**.
 * 🔒 Como a tabela, identifica o paciente só pelo ID (regra 3 de `02-alertas.md`).
 */
const AlertDetailsModal: React.FC<AlertDetailsModalProps> = ({
  alert,
  thresholds,
  onOpenPatient,
  onTreat,
  canTreat,
}) => {
  const { closeModal } = useModal();

  // Gráfico só para sinais do corpo com métrica no metrics service: silêncio,
  // score e perda de peso não têm uma série de leituras para desenhar.
  const chartConfig =
    alert.category === AlertCategoryEnum.BODY_SIGNS
      ? alertChartConfig(alert.parameter)
      : null;

  const [history, setHistory] = useState<AlertTriggersResponse>();
  const [view, setView] = useState<'list' | 'chart'>('list');

  useCurrentEffect(
    (isCurrent) => {
      listAlertTriggers(alert.id, TRIGGERS_LIMIT).then((data) => {
        if (isCurrent()) {
          setHistory(data);

          // Alerta sem leituras próprias (o de sono nasce da varredura das noites,
          // que não grava gatilho) abre no gráfico: a lista estaria vazia.
          if (data.total === 0 && chartConfig) {
            setView('chart');
          }
        }
      });
    },
    [alert.id, chartConfig],
  );

  const ruler = rulerFor(thresholds, alert.parameter, alert.severity);

  return (
    <Container data-alert-details>
      <AnchorModalTop />
      <h1>{getAlertLabels.parameter(alert.parameter)}</h1>

      <Badges>
        <Badge color={getAlertLabels.severityColor(alert.severity)}>
          {getAlertLabels.severity(alert.severity)}
        </Badge>
        <Badge color={getAlertLabels.categoryColor(alert.category)}>
          {getAlertLabels.category(alert.category)}
        </Badge>
        <Badge color={STATE_COLOR}>{getAlertLabels.state(alert.state)}</Badge>
      </Badges>

      <Summary>
        <SummaryRow>
          <span>Paciente</span>
          <span>
            <PatientLink
              type="button"
              title="Abrir prontuário"
              onClick={() => {
                closeModal();
                onOpenPatient(alert);
              }}
            >
              {alert.patientId.slice(0, 8)}
            </PatientLink>
            {alert.age !== null && ` · ${alert.age} anos`}
          </span>
        </SummaryRow>

        <SummaryRow>
          <span>Condições</span>
          {alert.conditions.length > 0 ? (
            <ConditionList>
              {alert.conditions.map((condition) => (
                <li key={condition}>{condition}</li>
              ))}
            </ConditionList>
          ) : (
            <Muted>—</Muted>
          )}
        </SummaryRow>

        <SummaryRow>
          <span>Leitura</span>
          <strong>{alert.reading || '—'}</strong>
        </SummaryRow>

        {ruler && (
          <SummaryRow>
            <span>Régua</span>
            <span>
              {getAlertLabels.severity(alert.severity)}: {ruler}
            </span>
          </SummaryRow>
        )}

        <SummaryRow>
          <span>Aberto em</span>
          <span>{formatDateTime(alert.occurredAt)}</span>
        </SummaryRow>

        <SummaryRow>
          <span>Última leitura</span>
          <span>{formatDateTime(alert.lastTriggeredAt)}</span>
        </SummaryRow>

        {alert.closeReason && (
          <SummaryRow>
            <span>Desfecho</span>
            <span>{getAppointmentOutcomeLabel(alert.closeReason)}</span>
          </SummaryRow>
        )}
      </Summary>

      {!history ? (
        <SpinnerBox>
          <Spinner />
        </SpinnerBox>
      ) : (
        (history.total > 0 || chartConfig) && (
          <Triggers>
            <SectionHeader>
              <h2>
                {view === 'chart' && chartConfig
                  ? 'Últimas leituras'
                  : `Leituras do alerta (${history.total})`}
              </h2>
              {chartConfig && (
                <ViewSwitch role="group" aria-label="Visualização das leituras">
                  <ViewOption
                    type="button"
                    active={view === 'list'}
                    aria-pressed={view === 'list'}
                    onClick={() => setView('list')}
                  >
                    Lista
                  </ViewOption>
                  <ViewOption
                    type="button"
                    active={view === 'chart'}
                    aria-pressed={view === 'chart'}
                    onClick={() => setView('chart')}
                  >
                    Gráfico
                  </ViewOption>
                </ViewSwitch>
              )}
            </SectionHeader>

            <AnimatedHeight>
              {view === 'chart' && chartConfig ? (
                <MetricChart
                  patientId={alert.patientId}
                  parameter={alert.parameter}
                  config={chartConfig}
                  thresholds={thresholds}
                  anchor={alert.lastTriggeredAt}
                  fallback={history.triggers}
                />
              ) : (
                <>
                  {history.total === 0 && (
                    <EmptyList>
                      Nenhuma leitura registrada neste alerta.
                    </EmptyList>
                  )}
                  <TriggerList hidden={history.total === 0}>
                    {history.triggers.map((trigger) => (
                      <TriggerRow key={trigger.id}>
                        <time dateTime={trigger.measuredAt}>
                          {format(
                            parseISO(trigger.measuredAt),
                            'dd/MM HH:mm:ss',
                          )}
                        </time>
                        <span>{trigger.reading}</span>
                        {trigger.severity ? (
                          <Badge
                            color={getAlertLabels.severityColor(
                              trigger.severity,
                            )}
                          >
                            {getAlertLabels.severity(trigger.severity)}
                          </Badge>
                        ) : (
                          <Muted>—</Muted>
                        )}
                      </TriggerRow>
                    ))}
                  </TriggerList>
                  {history.total > history.triggers.length && (
                    <Note>
                      As {history.triggers.length} leituras mais recentes de{' '}
                      {history.total}.
                    </Note>
                  )}
                </>
              )}
            </AnimatedHeight>
          </Triggers>
        )
      )}

      <Actions>
        <WhatsappAction alert={alert} />

        {alert.state === AlertStateEnum.IN_QUEUE && canTreat && (
          <TreatButton type="button" onClick={() => onTreat(alert)}>
            Tratar
          </TreatButton>
        )}
      </Actions>

      {/*
        À vista, e não só no title: aqui há espaço, e quem abriu o informativo para
        tratar precisa saber por que não pode antes de procurar o botão.
      */}
      {alert.state === AlertStateEnum.IN_QUEUE && !canTreat && (
        <Note>{CANNOT_TREAT_MESSAGE}</Note>
      )}
    </Container>
  );
};

export default AlertDetailsModal;
